import { useHandler } from "@redotech/react-util/hook";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { createContext, memo, ReactNode, useState } from "react";
import { CancelOrderModal } from "./cancel-order-modal";

export const CancelOrderModalContext = createContext<
  (groups: readonly FulfillmentGroupData[]) => void
>(() => {});

export const CancelOrderModalProvider = memo(function CancelOrderModalProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [groups, setGroups] = useState<readonly FulfillmentGroupData[]>([]);
  const [open, setOpen] = useState(false);
  const [onOrdersCancelled, setOnOrdersCancelled] = useState<
    ((groupIds: string[]) => void) | undefined
  >();

  const openModalWithGroups = (
    newGroups: readonly FulfillmentGroupData[],
    onOrdersCancelled?: (groupIds: string[]) => void,
  ) => {
    setGroups(newGroups);
    setOnOrdersCancelled(() => onOrdersCancelled);
    setOpen(true);
  };

  const onClose = useHandler(() => {
    setOpen(false);
    setGroups([]);
    setOnOrdersCancelled(undefined);
  });

  const onConfirm = useHandler(async () => {
    if (onOrdersCancelled) {
      try {
        await onOrdersCancelled(groups.map((g) => g._id));
        onClose();
      } catch (error) {
        console.error("Error cancelling orders", error);
      }
    }
  });

  const orderNames = groups.flatMap((g) => g.orders.map((o) => o.name));

  return (
    <CancelOrderModalContext.Provider value={openModalWithGroups}>
      {children}
      {groups.length > 0 && (
        <CancelOrderModal
          groups={groups}
          isOpen={open}
          onClose={onClose}
          onConfirm={onConfirm}
          orderNames={orderNames}
        />
      )}
    </CancelOrderModalContext.Provider>
  );
});
